import { IonRouterLink } from '@ionic/react';
import { FullPost, calcDateSpanDescription, isSubscriber } from 'model';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { colorFromCreated } from '../util/colorFromId';
import { getInitials } from '../util/initials';
import styles from './PostHeader.module.css';
import { PostMenu } from './PostMenu';
import { PostTypeIcon } from './PostTypeIcon';
import { ProfileImage } from './ProfileImage';
import { TopicButton } from './TopicButton';
import { IconCheckCircle } from './QuirkIcons/index.js';
import { useCallback } from 'react';
import { useTrackPostViewMutation } from '../api/PostApi.js';
import { useOnElementEntersView } from '../hooks/useOnElementEntersView.js';
import { useSubscriptionsEnabled } from '../hooks/useSubscriptionsEnabled.js';
export interface PostHeaderProps {
	post: FullPost;
	detail?: boolean;
	compact?: boolean;
	postDate?: string;
}

export function PostHeader({ post, compact = false, postDate }: PostHeaderProps): JSX.Element {
	const user = useCurrentUser();
	const [trackView] = useTrackPostViewMutation();
	const ref = useOnElementEntersView(
		useCallback(() => {
			trackView({ postId: post._id });
		}, [post._id, trackView]),
		0.9, // 90% of the element is visible
		true // only fire one time
	);
	const subscriptionsEnabled = useSubscriptionsEnabled();
	return (
		<>
			<div className={styles.PostHeader} data-testid={`PostHeader-${post._id}`} ref={ref}>
				<div className={styles.authorImage}>
					<IonRouterLink routerLink={`/main/settings/userprofile/${post.author}`}>
						<ProfileImage
							testId={`profilePic-${post.fullAuthor?._id}`}
							size="small"
							url={
								post.fullAuthor.profileImage
									? `/api/users/profileImage/${post.fullAuthor._id}/${post.fullAuthor.profileImage}`
									: undefined
							}
							text={getInitials(post.fullAuthor.username)}
							background={colorFromCreated(post.fullAuthor.created)}
							shape="circle"
							statusIcon={
								subscriptionsEnabled && isSubscriber(post.fullAuthor) ? <IconCheckCircle /> : undefined
							}
						/>
					</IonRouterLink>
				</div>
				<div className={styles.details}>
					<div className={styles.row}>
						<div className={styles.name} data-testid={`PostAuthor-${post._id}`}>
							{post.fullAuthor.username}
						</div>
						<div className={styles.postMenuContainer}>
							{!compact && <PostMenu post={post} userid={user._id} />}
						</div>
					</div>
					<div className={styles.row}>
						<span className={styles.typeDate}>
							<PostTypeIcon postType={post.postType} />
							<time data-testid={`PostDate-${post._id}`}>
								{calcDateSpanDescription(postDate ?? post.updated)}
							</time>
						</span>

						{!compact && post.topic && (
							<TopicButton topic={post.topic} testId={`TopicButton-${post._id}`} />
						)}
					</div>
				</div>
			</div>
		</>
	);
}
